import {
  ChevronLeft,
  ChevronRight,
  X,
} from "lucide-react-native";
import React from "react";
import {
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export type ItemImageViewerEntry = {
  key: string;
  title: string;
  uri: string;
  priceText?: string;
  description?: string;
};

type Props = {
  visible: boolean;
  items: ItemImageViewerEntry[];
  initialIndex: number;
  onClose: () => void;
};

function clampIndex(index: number, total: number) {
  if (total <= 0) return 0;
  return Math.max(0, Math.min(total - 1, index));
}

export default function ItemImageViewerModal({
  visible,
  items,
  initialIndex,
  onClose,
}: Props) {
  const scrollRef = React.useRef<ScrollView | null>(null);
  const wasVisibleRef = React.useRef(false);
  const lastInitialIndexRef = React.useRef(initialIndex);
  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();
  const isDesktop = width >= 1024;
  const pageWidth = Math.max(1, width);
  const total = items.length;
  const [activeIndex, setActiveIndex] = React.useState(() =>
    clampIndex(initialIndex, total)
  );
  const activeIndexRef = React.useRef(activeIndex);

  const setActiveIndexSafely = React.useCallback(
    (index: number) => {
      const nextIndex = clampIndex(index, total);

      if (activeIndexRef.current === nextIndex) {
        return nextIndex;
      }

      activeIndexRef.current = nextIndex;
      setActiveIndex(nextIndex);

      return nextIndex;
    },
    [total]
  );

  const scrollToIndex = React.useCallback(
    (index: number, animated: boolean) => {
      scrollRef.current?.scrollTo({
        x: clampIndex(index, total) * pageWidth,
        y: 0,
        animated,
      });
    },
    [pageWidth, total]
  );

  const updateActiveIndexFromOffset = React.useCallback(
    (offsetX: number) => {
      setActiveIndexSafely(Math.round(offsetX / pageWidth));
    },
    [pageWidth, setActiveIndexSafely]
  );

  React.useEffect(() => {
    if (!visible || total <= 0) {
      wasVisibleRef.current = false;
      return;
    }

    const shouldJumpToInitial =
      !wasVisibleRef.current || lastInitialIndexRef.current !== initialIndex;

    wasVisibleRef.current = true;
    lastInitialIndexRef.current = initialIndex;

    if (shouldJumpToInitial) {
      const nextIndex = setActiveIndexSafely(initialIndex);
      requestAnimationFrame(() => scrollToIndex(nextIndex, false));
    }
  }, [initialIndex, scrollToIndex, setActiveIndexSafely, total, visible]);

  React.useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  React.useEffect(() => {
    if (!visible || total <= 0) return;
    const nextIndex = setActiveIndexSafely(activeIndexRef.current);
    requestAnimationFrame(() => scrollToIndex(nextIndex, false));
  }, [pageWidth, scrollToIndex, setActiveIndexSafely, total, visible]);

  React.useEffect(() => {
    if (Platform.OS !== "web" || !visible || !isDesktop) return;

    type DocumentWithKeys = {
      addEventListener?: (
        type: "keydown",
        listener: (event: { key?: string }) => void
      ) => void;
      removeEventListener?: (
        type: "keydown",
        listener: (event: { key?: string }) => void
      ) => void;
    };

    const documentRef = (
      globalThis as typeof globalThis & { document?: DocumentWithKeys }
    ).document;

    if (!documentRef?.addEventListener || !documentRef.removeEventListener) {
      return;
    }

    const handleKeyDown = (event: { key?: string }) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    documentRef.addEventListener("keydown", handleKeyDown);

    return () => {
      documentRef.removeEventListener?.("keydown", handleKeyDown);
    };
  }, [isDesktop, onClose, visible]);

  const goToIndex = React.useCallback(
    (index: number) => {
      const nextIndex = setActiveIndexSafely(index);
      scrollToIndex(nextIndex, true);
    },
    [scrollToIndex, setActiveIndexSafely]
  );

  if (!visible || total <= 0) return null;

  const activeItem = items[activeIndex] ?? items[0];
  const canGoPrev = activeIndex > 0;
  const canGoNext = activeIndex < total - 1;
  const topPadding = Math.max(insets.top + 10, 18);
  const bottomPadding = Math.max(insets.bottom + 18, 28);
  const slideMinHeight = Math.max(240, height - topPadding - bottomPadding);
  const imageHeight = isDesktop
    ? Math.max(360, slideMinHeight - 120)
    : Math.max(240, Math.min(slideMinHeight * 0.72, width * 1.18));

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={[styles.topBar, { paddingTop: topPadding }]}>
          <Text style={styles.caption} numberOfLines={1}>
            {activeItem.title || "Item"} {"\u00b7"} {activeIndex + 1} of {total}
          </Text>

          <Pressable
            onPress={onClose}
            style={({ pressed }) => [
              styles.closeButton,
              pressed ? styles.buttonPressed : null,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Close image viewer"
          >
            <X size={24} color="#ffffff" strokeWidth={2.5} />
          </Pressable>
        </View>

        <View style={styles.viewerBody}>
          <ScrollView
            ref={scrollRef}
            horizontal
            pagingEnabled
            showsHorizontalScrollIndicator={false}
            scrollEventThrottle={16}
            decelerationRate="fast"
            onScroll={(event) => {
              updateActiveIndexFromOffset(event.nativeEvent.contentOffset.x);
            }}
            onScrollEndDrag={(event) => {
              updateActiveIndexFromOffset(event.nativeEvent.contentOffset.x);
            }}
            onMomentumScrollEnd={(event) => {
              updateActiveIndexFromOffset(event.nativeEvent.contentOffset.x);
            }}
            onLayout={() => scrollToIndex(activeIndexRef.current, false)}
          >
            {items.map((item) => {
              const priceText = String(item.priceText ?? "").trim();
              const description = String(item.description ?? "").trim();

              return (
                <View
                  key={item.key}
                  style={[
                    styles.slidePage,
                    {
                      width: pageWidth,
                      minHeight: slideMinHeight,
                    },
                  ]}
                >
                  <ScrollView
                    nestedScrollEnabled
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={[
                      styles.slideScrollContent,
                      {
                        minHeight: slideMinHeight,
                        paddingHorizontal: isDesktop ? 112 : 18,
                        paddingTop: isDesktop ? 24 : 16,
                        paddingBottom: bottomPadding,
                      },
                    ]}
                  >
                    <Image
                      source={{ uri: item.uri }}
                      style={[styles.image, { height: imageHeight }]}
                      resizeMode="contain"
                    />

                    {priceText || description ? (
                      <View style={styles.itemDetails}>
                        {priceText ? (
                          <Text style={styles.priceText}>{priceText}</Text>
                        ) : null}

                        {description ? (
                          <Text style={styles.descriptionText}>
                            {description}
                          </Text>
                        ) : null}
                      </View>
                    ) : null}
                  </ScrollView>
                </View>
              );
            })}
          </ScrollView>

          {isDesktop && total > 1 ? (
            <>
              <Pressable
                onPress={() => goToIndex(activeIndex - 1)}
                disabled={!canGoPrev}
                style={({ pressed }) => [
                  styles.arrowButton,
                  styles.arrowLeft,
                  !canGoPrev ? styles.arrowDisabled : null,
                  pressed && canGoPrev ? styles.buttonPressed : null,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Previous image"
              >
                <ChevronLeft size={32} color="#ffffff" strokeWidth={2.5} />
              </Pressable>

              <Pressable
                onPress={() => goToIndex(activeIndex + 1)}
                disabled={!canGoNext}
                style={({ pressed }) => [
                  styles.arrowButton,
                  styles.arrowRight,
                  !canGoNext ? styles.arrowDisabled : null,
                  pressed && canGoNext ? styles.buttonPressed : null,
                ]}
                accessibilityRole="button"
                accessibilityLabel="Next image"
              >
                <ChevronRight size={32} color="#ffffff" strokeWidth={2.5} />
              </Pressable>
            </>
          ) : null}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.96)",
  },

  topBar: {
    minHeight: 68,
    paddingHorizontal: 16,
    paddingBottom: 10,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.12)",
    zIndex: 2,
  },

  caption: {
    flex: 1,
    minWidth: 0,
    paddingRight: 12,
    color: "#ffffff",
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "900",
  },

  closeButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.22)",
    backgroundColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
  },

  viewerBody: {
    flex: 1,
    minHeight: 0,
  },

  slidePage: {
    flex: 1,
    minHeight: 0,
  },

  slideScrollContent: {
    alignItems: "center",
    justifyContent: "flex-start",
  },

  image: {
    width: "100%",
    backgroundColor: "rgba(255,255,255,0.02)",
  },

  itemDetails: {
    width: "100%",
    maxWidth: 760,
    alignSelf: "center",
    alignItems: "center",
    paddingTop: 18,
    gap: 10,
  },

  priceText: {
    color: "#ffffff",
    fontSize: 21,
    lineHeight: 27,
    fontWeight: "900",
    textAlign: "center",
  },

  descriptionText: {
    color: "rgba(255,255,255,0.78)",
    fontSize: 15,
    lineHeight: 22,
    fontWeight: "600",
    textAlign: "center",
  },

  arrowButton: {
    position: "absolute",
    top: "50%",
    width: 58,
    height: 58,
    marginTop: -29,
    borderRadius: 29,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.22)",
    backgroundColor: "rgba(255,255,255,0.14)",
    alignItems: "center",
    justifyContent: "center",
  },

  arrowLeft: {
    left: 26,
  },

  arrowRight: {
    right: 26,
  },

  arrowDisabled: {
    opacity: 0.28,
  },

  buttonPressed: {
    opacity: 0.75,
    transform: [{ scale: 0.98 }],
  },
});
